import { Pattern, AlignmentSettings } from '../types/pattern';
import { STORAGE_KEYS } from './constants';
import { generateId } from './generateId';

const defaultAlignmentSettings: AlignmentSettings = {
  showGuides: true,
  radialAngleStep: 30,
  circleStep: 30,
  wedges: 12,
  circlesCount: 10,
  stitchScale: 1.5,
};

function isPoint(v: unknown): v is { x: number; y: number } {
  return typeof v === 'object' && v !== null && typeof (v as any).x === 'number' && typeof (v as any).y === 'number';
}

function isStitch(v: unknown): boolean {
  if (typeof v !== 'object' || v === null) return false;
  const s = v as any;
  return typeof s.type === 'string' && typeof s.color === 'string' && isPoint(s.position) && typeof s.angle === 'number';
}

export function validatePattern(parsed: unknown): Pattern | null {
  if (typeof parsed !== 'object' || parsed === null) return null;
  const p = parsed as any;

  if (typeof p.name !== 'string') return null;
  if (!isPoint(p.center)) return null;
  if (!Array.isArray(p.stitches)) return null;

  // Migrate stitches: accept both Stitch[][] (legacy) and Stitch[] (current)
  let stitches: any[];
  if (p.stitches.length > 0 && Array.isArray(p.stitches[0])) {
    // Legacy Stitch[][] format — flatten
    stitches = p.stitches.flat();
  } else {
    stitches = p.stitches;
  }

  // Validate each stitch and ensure it has an id
  for (const s of stitches) {
    if (!isStitch(s)) return null;
    if (!s.id) s.id = generateId();
  }

  const alignmentSettings: AlignmentSettings = {
    ...defaultAlignmentSettings,
    ...p.alignmentSettings,
    stitchScale: p.alignmentSettings?.stitchScale ?? 1.5,
  };

  return {
    id: p.id || '1',
    name: p.name,
    stitches,
    center: p.center,
    radius: typeof p.radius === 'number' ? p.radius : 300,
    orientationCenter: isPoint(p.orientationCenter) ? p.orientationCenter : p.center,
    symmetryAxes: typeof p.symmetryAxes === 'number' ? p.symmetryAxes : 0,
    alignmentSettings,
    createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
    updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
  };
}

export const savePattern = (pattern: Pattern): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.PATTERN, JSON.stringify(pattern));
  } catch (error) {
    console.error('Error saving pattern:', error);
  }
};

export const loadPattern = (): Pattern | null => {
  try {
    const savedPattern = localStorage.getItem(STORAGE_KEYS.PATTERN);
    if (savedPattern) {
      const parsed = JSON.parse(savedPattern);
      return validatePattern(parsed);
    }
    return null;
  } catch (error) {
    console.error('Error loading pattern:', error);
    return null;
  }
};

export const exportPattern = (pattern: Pattern): void => {
  const json = JSON.stringify(pattern, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const safeName = pattern.name.replace(/[^a-zA-Z0-9_\-\s]/g, '').trim() || 'pattern';
  link.download = `${safeName}.doilyapp`;
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
};

export const importPattern = (): Promise<Pattern | null> => {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.doilyapp,.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) { resolve(null); return; }
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result as string);
          const validated = validatePattern(parsed);
          resolve(validated);
        } catch {
          resolve(null);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  });
};
